import { ReportData, AdSpendSource, SalesSource, TradeSales, JobData, ReportPeriod } from '../types/reportTypes';
import { aprilData } from './aprilData';
import { mayData } from './mayData';
import { juneData } from './juneData';

// Helper function to get month name
const getMonthName = (month: number): string => {
  return new Date(2000, month - 1).toLocaleString('default', { month: 'long' });
};

// Helper function to merge and de-duplicate sources by name
const mergeSourcesByName = <T extends { source: string }>(
  arrays: T[][],
  propertyExtractor: (item: T) => { [key: string]: number }
): T[] => {
  const sourceMap = new Map<string, { source: string } & { [key: string]: number }>();

  arrays.forEach(array => {
    array.forEach(item => {
      const sourceName = item.source;
      const extracted = propertyExtractor(item);
      
      if (!sourceMap.has(sourceName)) {
        const initialObject = { source: sourceName } as { source: string } & { [key: string]: number };
        
        // Add properties with initial value of 0
        Object.keys(extracted).forEach(key => {
          initialObject[key] = 0;
        });
        
        sourceMap.set(sourceName, initialObject);
      }
      
      const existing = sourceMap.get(sourceName)!;
      
      // Add values from current item to existing totals
      Object.entries(extracted).forEach(([key, value]) => {
        existing[key] = (existing[key] || 0) + value;
      });
    });
  });

  return Array.from(sourceMap.values()) as unknown as T[];
};

// Aggregate KPI data from all three months
const aggregateKPIs = () => {
  return {
    adSpend: aprilData.kpis.adSpend + mayData.kpis.adSpend + juneData.kpis.adSpend,
    totalSales: aprilData.kpis.totalSales + mayData.kpis.totalSales + juneData.kpis.totalSales,
    grossProfit: aprilData.kpis.grossProfit + mayData.kpis.grossProfit + juneData.kpis.grossProfit,
    salesCommissions: aprilData.kpis.salesCommissions + mayData.kpis.salesCommissions + juneData.kpis.salesCommissions,
    managerFees: aprilData.kpis.managerFees + mayData.kpis.managerFees + juneData.kpis.managerFees,
    companyOH: aprilData.kpis.companyOH + mayData.kpis.companyOH + juneData.kpis.companyOH,
    netProfit: aprilData.kpis.netProfit + mayData.kpis.netProfit + juneData.kpis.netProfit,
    // Calculate net profit percentage over the entire quarter
    netProfitPercent: (aprilData.kpis.netProfit + mayData.kpis.netProfit + juneData.kpis.netProfit) / 
                      (aprilData.kpis.totalSales + mayData.kpis.totalSales + juneData.kpis.totalSales),
    // Calculate gross profit percentage over the entire quarter
    grossProfitPercent: (aprilData.kpis.grossProfit + mayData.kpis.grossProfit + juneData.kpis.grossProfit) / 
                        (aprilData.kpis.totalSales + mayData.kpis.totalSales + juneData.kpis.totalSales)
  };
};

// Combine ad spend data from all three months
const aggregateAdSpendBySource = (): AdSpendSource[] => {
  return mergeSourcesByName<AdSpendSource>(
    [aprilData.adSpendBySource, mayData.adSpendBySource, juneData.adSpendBySource],
    (item) => ({ spend: item.spend })
  ).sort((a, b) => b.spend - a.spend);
};

// Combine sales data by source from all three months
const aggregateSalesBySource = (): SalesSource[] => {
  return mergeSourcesByName<SalesSource>(
    [aprilData.salesBySource, mayData.salesBySource, juneData.salesBySource],
    (item) => ({ totalSales: item.totalSales, count: item.count, grossProfit: item.grossProfit })
  ).sort((a, b) => b.totalSales - a.totalSales);
};

// Combine sales data by trade from all three months
const aggregateSalesByTrade = (): TradeSales[] => {
  // Create a map to store aggregated trade data
  const tradeMap = new Map<string, TradeSales>();
  
  // Process each month's data
  [aprilData.salesByTrade, mayData.salesByTrade, juneData.salesByTrade].forEach(monthData => {
    monthData.forEach(tradeItem => {
      const tradeName = tradeItem.trade;
      
      if (!tradeMap.has(tradeName)) {
        tradeMap.set(tradeName, {
          trade: tradeName,
          totalSales: 0,
          count: 0,
          grossProfit: 0
        });
      }
      
      const existing = tradeMap.get(tradeName)!;
      existing.totalSales += tradeItem.totalSales;
      existing.count += tradeItem.count;
      existing.grossProfit += tradeItem.grossProfit;
    });
  });
  
  // Sort by total sales descending
  return Array.from(tradeMap.values()).sort((a, b) => b.totalSales - a.totalSales);
};

// Combine job data from all three months
const aggregateJobData = (): JobData[] => {
  return [...aprilData.jobData, ...mayData.jobData, ...juneData.jobData];
};

// Export the Q2 2025 quarterly report data
export const q2Data: ReportData = {
  kpis: aggregateKPIs(),
  adSpendBySource: aggregateAdSpendBySource(),
  salesBySource: aggregateSalesBySource(),
  salesByTrade: aggregateSalesByTrade(),
  jobData: aggregateJobData(),
  period: {
    month: 4, // First month of Q2 (April)
    year: 2025,
    quarter: 2, // Q2
    startMonth: 4, // April
    endMonth: 6   // June
  }
};

// Additional data for trend analysis in the quarterly report
export interface Q2TrendData {
  monthlyTrends: {
    month: string;
    totalSales: number;
    grossProfit: number;
    netProfit: number;
    adSpend: number;
    roi: number;
    grossMarginPct: number;
    netMarginPct: number;
    salesCount: number;
    avgSaleValue: number;
  }[];
  sourcePerformance: SourcePerformance[];
  leadSourceTrends: {
    source: string;
    april: { sales: number; count: number; roi: number };
    may: { sales: number; count: number; roi: number };
    june: { sales: number; count: number; roi: number };
    q2Total: { sales: number; count: number; roi: number };
  }[];
}

// Extended interface for source performance
interface SourcePerformance extends SalesSource {
  adSpend: number;
  roi: number;
  costPerSale: number;
}

// Generate trend data for Q2 analysis
export const q2TrendData: Q2TrendData = {
  monthlyTrends: [
    {
      month: "April",
      totalSales: aprilData.kpis.totalSales,
      grossProfit: aprilData.kpis.grossProfit,
      netProfit: aprilData.kpis.netProfit,
      adSpend: aprilData.kpis.adSpend,
      roi: aprilData.kpis.adSpend > 0 ? aprilData.kpis.grossProfit / aprilData.kpis.adSpend : 0,
      grossMarginPct: aprilData.kpis.grossProfitPercent,
      netMarginPct: aprilData.kpis.netProfitPercent,
      salesCount: aprilData.jobData.length,
      avgSaleValue: aprilData.kpis.totalSales / aprilData.jobData.length
    },
    {
      month: "May",
      totalSales: mayData.kpis.totalSales,
      grossProfit: mayData.kpis.grossProfit,
      netProfit: mayData.kpis.netProfit,
      adSpend: mayData.kpis.adSpend,
      roi: mayData.kpis.adSpend > 0 ? mayData.kpis.grossProfit / mayData.kpis.adSpend : 0,
      grossMarginPct: mayData.kpis.grossProfitPercent,
      netMarginPct: mayData.kpis.netProfitPercent,
      salesCount: mayData.jobData.length,
      avgSaleValue: mayData.kpis.totalSales / mayData.jobData.length
    },
    {
      month: "June",
      totalSales: juneData.kpis.totalSales,
      grossProfit: juneData.kpis.grossProfit,
      netProfit: juneData.kpis.netProfit,
      adSpend: juneData.kpis.adSpend,
      roi: juneData.kpis.adSpend > 0 ? juneData.kpis.grossProfit / juneData.kpis.adSpend : 0,
      grossMarginPct: juneData.kpis.grossProfitPercent,
      netMarginPct: juneData.kpis.netProfitPercent,
      salesCount: juneData.jobData.length,
      avgSaleValue: juneData.kpis.totalSales / juneData.jobData.length
    }
  ],
  
  // Calculate source performance with ROI
  sourcePerformance: (() => {
    const salesBySource = aggregateSalesBySource();
    const adSpendBySource = aggregateAdSpendBySource();
    
    return salesBySource.map(source => {
      const adSpendItem = adSpendBySource.find(ad => 
        ad.source.toLowerCase().includes(source.source.toLowerCase()) || 
        source.source.toLowerCase().includes(ad.source.toLowerCase())
      );
      
      const adSpend = adSpendItem?.spend || 0;
      const roi = adSpend > 0 ? source.grossProfit / adSpend : 0;
      const costPerSale = source.count > 0 ? adSpend / source.count : 0;
      
      return {
        ...source,
        adSpend,
        roi,
        costPerSale
      };
    }).sort((a, b) => b.roi - a.roi);
  })(),
  
  // Generate lead source trends
  leadSourceTrends: (() => {
    // Get unique sources across all months
    const allSources = new Set<string>();
    [aprilData.salesBySource, mayData.salesBySource, juneData.salesBySource].forEach(monthSources => {
      monthSources.forEach(source => allSources.add(source.source));
    });
    
    return Array.from(allSources).map(sourceName => {
      // Find data for each month
      const aprilSource = aprilData.salesBySource.find(s => s.source === sourceName) || 
                         { totalSales: 0, count: 0, grossProfit: 0 };
      const maySource = mayData.salesBySource.find(s => s.source === sourceName) || 
                       { totalSales: 0, count: 0, grossProfit: 0 };
      const juneSource = juneData.salesBySource.find(s => s.source === sourceName) || 
                        { totalSales: 0, count: 0, grossProfit: 0 };
      
      // Find ad spend for ROI calculation
      const aprilAdSpend = aprilData.adSpendBySource.find(ad => 
        ad.source.toLowerCase().includes(sourceName.toLowerCase()) || 
        sourceName.toLowerCase().includes(ad.source.toLowerCase())
      )?.spend || 0;
      
      const mayAdSpend = mayData.adSpendBySource.find(ad => 
        ad.source.toLowerCase().includes(sourceName.toLowerCase()) || 
        sourceName.toLowerCase().includes(ad.source.toLowerCase())
      )?.spend || 0;
      
      const juneAdSpend = juneData.adSpendBySource.find(ad => 
        ad.source.toLowerCase().includes(sourceName.toLowerCase()) || 
        sourceName.toLowerCase().includes(ad.source.toLowerCase())
      )?.spend || 0;
      
      const totalAdSpend = aprilAdSpend + mayAdSpend + juneAdSpend;
      const totalGrossProfit = aprilSource.grossProfit + maySource.grossProfit + juneSource.grossProfit;
      
      return {
        source: sourceName,
        april: {
          sales: aprilSource.totalSales,
          count: aprilSource.count,
          roi: aprilAdSpend > 0 ? aprilSource.grossProfit / aprilAdSpend : 0
        },
        may: {
          sales: maySource.totalSales,
          count: maySource.count,
          roi: mayAdSpend > 0 ? maySource.grossProfit / mayAdSpend : 0
        },
        june: {
          sales: juneSource.totalSales,
          count: juneSource.count,
          roi: juneAdSpend > 0 ? juneSource.grossProfit / juneAdSpend : 0
        },
        q2Total: {
          sales: aprilSource.totalSales + maySource.totalSales + juneSource.totalSales,
          count: aprilSource.count + maySource.count + juneSource.count,
          roi: totalAdSpend > 0 ? totalGrossProfit / totalAdSpend : 0
        }
      };
    }).sort((a, b) => b.q2Total.sales - a.q2Total.sales);
  })()
};